import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useErrorBoundary } from "react-error-boundary";
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from "@shared/lib/utils";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { Account, AccountSearch } from "@models/account";
import { ACCOUNT_BROWSE_PER_PAGE, buildAccountSort } from "@features/tickets/api/accountsBrowse.utils";
import { useSearchAccountsBrowseQuery } from "@features/tickets/api/accounts.api";
import {
  DEFAULT_SORT_KEY,
  parseAccountBrowseUrlParams,
  syncAccountBrowseUrlParams,
} from "@features/tickets/shared/utils/accountBrowseUrlParams";
import { useEntityBrowse } from "./useEntityBrowse";
import { useBrowsePagination } from "./useBrowsePagination";
import { isBrowseCacheEligible } from "./useBrowseCacheEligible";

/**
 * Shared browse hook for account-type ticket pages (incomes, banks, etc.).
 */
type UseAccountTypeBrowseOptions = {
  accountType: NonNullable<AccountSearch["account_type"]>;
  withRelations?: boolean;
};

/**
 * Shared browse state for account-type ticket pages (incomes, banks, etc.).
 */
export function useAccountTypeBrowse({ accountType, withRelations }: UseAccountTypeBrowseOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showBoundary } = useErrorBoundary();

  const urlParams = useMemo(() => parseAccountBrowseUrlParams(searchParams), [searchParams]);

  const [data, setData] = useState<Account[]>([]);
  const [sortKey, setSortKey] = useState(urlParams.sortKey || DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(urlParams.sortMethod);
  const [queryFilter, setQueryFilter] = useState<Partial<AccountSearch>>(
    urlParams.filters ?? { account_type: accountType },
  );
  const pagination = useBrowsePagination(ACCOUNT_BROWSE_PER_PAGE);

  const accountSearchForm = useForm<AccountSearch>({
    defaultValues: {
      account_type: accountType,
      ...(urlParams.filters ?? {}),
    },
  });

  const sort = buildAccountSort(sortKey, sortMethod);

  const {
    data: queryData,
    isFetching: isLoading,
    error,
  } = useSearchAccountsBrowseQuery(
    {
      accountType,
      withRelations,
      filter: {
        account_type: accountType,
        ...queryFilter,
      },
      sort: [sort],
      per_page: pagination.perPage,
      cursor: pagination.cursor,
      useCache: isBrowseCacheEligible({
        filters: { account_type: accountType, ...queryFilter },
        sortKey,
        sortMethod,
        cursor: pagination.cursor,
        allowedFilterKeys: ["account_type"],
      }),
    },
    // {
    //   refetchOnMountOrArgChange: true,
    // },
  );

  useEffect(() => {
    if (queryData) {
      setData(queryData.data);
    }
  }, [queryData]);

  useEffect(() => {
    if (error) {
      handleErrorWithBoundary(error, showBoundary);
    }
  }, [error, showBoundary]);

  const onSearch: SubmitHandler<AccountSearch> = (formData) => {
    pagination.resetPagination();
    syncAccountBrowseUrlParams(setSearchParams, formData, sortKey, sortMethod);
    setQueryFilter(deleteEmptyFieldsFromObject(formData));
  };

  useEntityBrowse({
    form: accountSearchForm,
    sortKey,
    sortMethod,
    onSearch,
  });

  function handleSortKeyChanged(value: string) {
    pagination.resetPagination();
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    pagination.resetPagination();
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function handleNextPage() {
    pagination.handleNextPage(queryData?.meta.next_cursor);
  }

  function handlePrevPage() {
    pagination.handlePrevPage(queryData?.meta.prev_cursor);
  }

  function reset() {
    const values = setAllValuesToNull(accountSearchForm.getValues());

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        accountSearchForm.setValue(key as keyof AccountSearch, values[key as keyof AccountSearch]);
      }
    }

    accountSearchForm.setValue("account_type", accountType);
  }

  return {
    accountSearchForm,
    data,
    isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    page: pagination.page,
    perPage: pagination.perPage,
    hasNextPage: Boolean(queryData?.meta.next_cursor),
    hasPrevPage: Boolean(queryData?.meta.prev_cursor),
    handleNextPage,
    handlePrevPage,
    handleRowsPerPageChange: pagination.handleRowsPerPageChange,
    reset,
  };
}
